import { PointOfInterestMultiStructureInput } from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import { ExpandMoreRounded } from '@mui/icons-material';
import { AccordionSummary, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDragToScroll } from '@/hooks';
import { DueDateWidget } from './DueDateWidget';
import { PaintDamageAlarmLevelWidget } from './PaintDamageAlarmLevelWidget';
import { PriorityWidget } from './PriorityWidget';
import { StatusWidget } from './StatusWidget';
import { SummaryAccordion, SummaryTitle, WidgetsContainer } from './styles';
import { POIWidgets } from '@/types';

const widgetComponents = {
  [POIWidgets.Status]: StatusWidget,
  [POIWidgets.InspectionPriority]: PriorityWidget,
  [POIWidgets.DueDate]: DueDateWidget,
  [POIWidgets.PaintDamageAlarmLevels]: PaintDamageAlarmLevelWidget,
};

type SummaryProps = {
  queryInput: PointOfInterestMultiStructureInput;
  visibleWidgets: POIWidgets[];
};

export const Summary = ({ queryInput, visibleWidgets }: SummaryProps) => {
  const [stackWidth, setStackWidth] = useState<number>(0);
  const { containerReference, handleMouseDown } = useDragToScroll();

  useEffect(() => {
    const currentContainer = containerReference.current;

    const resizeObserver = new ResizeObserver(() => {
      if (currentContainer?.offsetWidth) setStackWidth(currentContainer.offsetWidth);
    });

    if (currentContainer) resizeObserver.observe(currentContainer);

    return () => {
      if (currentContainer) resizeObserver.unobserve(currentContainer);
    };
  }, [containerReference]);

  const widgetWidth = useMemo(() => {
    if (stackWidth === 0) return 0;
    return `calc((${stackWidth}px - 5.6rem) / 3)`;
  }, [stackWidth]);

  return (
    <SummaryAccordion disableGutters defaultExpanded elevation={0}>
      <AccordionSummary
        sx={{ padding: '0px !important', minHeight: 'fit-content' }}
        expandIcon={
          <ExpandMoreRounded sx={{ color: abyssColors.primary[500], fontSize: '2.4rem' }} />
        }
      >
        <SummaryTitle>Summary</SummaryTitle>
      </AccordionSummary>
      <WidgetsContainer ref={containerReference} onMouseDown={handleMouseDown}>
        <Stack direction="row" alignItems="center" gap="2.4rem" width="fit-content">
          {visibleWidgets.map((widget) => {
            const WidgetComponent = widgetComponents[widget];
            if (!WidgetComponent) return <></>;
            return (
              <WidgetComponent key={widget} queryInput={queryInput} widgetWidth={widgetWidth} />
            );
          })}
        </Stack>
      </WidgetsContainer>
    </SummaryAccordion>
  );
};
